import { useEffect, useState } from 'react';
import { Grid, Typography, Button, useMediaQuery } from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import ProfileItemEmptyState from '../Profile/components/ProfileItemEmptyState';
import eventBus, { eventBusValues } from '../../../eventBus';
import ServicesChip from './ServicesChip';
import ServicesButton from './ServicesButton';
import { getPublicAdvisorRecurlyPlan } from '../../../services/advisor_services';

const useStyles = () => ({
  subtitle: {
    margin: '30px 0px 10px',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: -0.43,
    color: '#232B35',
  },
  serviceCard: {
    border: '1px solid #EFF0F7',
    borderRadius: '12px',
    width: '330px',
    padding: '24px',
    margin: '14px 15px 14px 0px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

function Services(props) {
  const { isFreeTrial } = useAccount();
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  
  const { services, advisor, isOwnProfile, publicProfile, triggerAddServices } =
  props;
  
  const { givenName } = advisor;
  const classes = useStyles();
  const [hideServices, setHideServices] = useState(true);

  function openModal() {
    eventBus.dispatch(eventBusValues.triggerCompanyUpgradeAction, {
      servicesText: true,
      origin: 'profile-advisory-rate-gates',
    });
  }

  useEffect(async() => {
    if (isOwnProfile) {
      setHideServices(false);
    } else if (!isOwnProfile && advisor.freeTrialEndsOn) {
      // hiding services for free trial users
      setHideServices(true);
    } else if (advisor.recurlyPlanCode) {
      // checking recurly plan for non-own profiles, hiding services for freemium plans
      try {
        const { data } = await getPublicAdvisorRecurlyPlan({
          recurlyPlanCode: advisor.recurlyPlanCode,
        });
        setHideServices(data.getRecurlyPlan.isFreemium || false);
      } catch (error) {
        console.error('Error fetching advisor recurly plan:', error);
        setHideServices(true);
      }
    }
  }, [isOwnProfile, isFreeTrial]);

  return (
    <>
      {services && services.length && !hideServices ? (
        <div
          style={{ margin: publicProfile ? null : '30px' }}
          id="advisor-services"
        >
          <Typography sx={classes.subtitle}>
            Advisory Rates & Services
            {isOwnProfile && isFreeTrial && (
              <ServicesButton
                clickAction={openModal}
                sx={{ display: { xxs: 'none', xs: 'inline-flex' } }}
              />
            )}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '-0.43px',
              color: '#646D7A',
              marginBottom: '11px',
            }}
          >
            {givenName} is available as an advisor, or for project-based work
            related to the following services:
          </Typography>

          {isOwnProfile && isFreeTrial && (
            <ServicesButton
              clickAction={openModal}
              sx={{
                height: '32px',
                width: '100%',
                display: { xxs: 'inline-flex', xs: 'none' },
              }}
            />
          )}
          <Grid
            container
            style={{
              marginBottom: '10px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {services.map((service, index) => (
              <div
                id={`advisor-service-${index + 1}`}
                className="advisor-service-card"
                key={index}
                style={classes.serviceCard}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '24px',
                    letterSpacing: '-0.43px',
                    color: '#646D7A',
                    marginBottom: '11px',
                  }}
                >
                  {service.description}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '24px',
                      textAlign: 'right',
                    }}
                  >
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(service.price)}
                  </Typography>
                  {isOwnProfile && isFreeTrial && (
                    <ServicesChip clickAction={openModal} />
                  )}
                </div>
              </div>
            ))}
          </Grid>
        </div>
      ) : null}

      {(!services || services.length === 0) &&
        props.isOwnProfile &&
        !publicProfile && (
          <>
            <ProfileItemEmptyState
              TitleComponent={() => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isXS ? 'column' : 'row',
                    marginBottom: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '20px',
                      lineHeight: '24px',
                      letterSpacing: '-0.43px',
                      color: '#232B35',
                      marginTop: '3px',
                    }}
                  >
                    Advisory Rates & Services
                  </Typography>
                  {triggerAddServices && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      id="services-button"
                      sx={{
                        marginLeft: { xxs: 0, xs: '20px' },
                        borderRadius: '40px',
                        height: '30px',
                        padding: '0 16px',
                        flexDirection: isXS ? 'column' : 'row',
                        mt: { xxs: '10px', xs: 0 },
                      }}
                      onClick={() => {
                        triggerAddServices();
                      }}
                    >
                      Add an Advisor Service
                    </Button>
                  )}
                </div>
              )}
              subtitle="Add custom advisory services with project-based rates. Include a description of the service, deliverables provided, and estimated timeframe."
              mainBoxStyle={{
                margin: '30px',
                paddingRight: { xxs: '42px', sm: '30px' },
              }}
            />
            <Grid
              container
              style={{
                margin: '20px 30px 30px',
                marginBottom: '10px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <div
                className="advisor-service-card"
                style={{
                  ...classes.serviceCard,
                  height: '130px',
                  marginTop: '0px',
                }}
              >
                <ProfileItemEmptyState
                  logs
                  items={[
                    {
                      width: '100%',
                    },
                    {
                      width: '100%',
                    },
                    {
                      width: '25%',
                      align: 'end',
                    },
                  ]}
                />
              </div>
              <div
                className="advisor-service-card"
                style={{
                  ...classes.serviceCard,
                  height: '130px',
                  marginTop: '0px',
                }}
              >
                <ProfileItemEmptyState
                  logs
                  items={[
                    {
                      width: '100%',
                    },
                    {
                      width: '100%',
                    },
                    {
                      width: '25%',
                      align: 'end',
                    },
                  ]}
                />
              </div>
            </Grid>
          </>
        )}
    </>
  );
}

export default Services;
